import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, ShieldCheck, FileCheck, Landmark, ShieldQuestion, Handshake, Truck, MapPin, User } from "lucide-react";
import { motion } from "framer-motion";

export default function SecureTradePage() {
  const { t } = useTranslation();
  const pillars = [
    { k: "verified_dealers", Icon: ShieldCheck },
    { k: "company_docs", Icon: FileCheck },
    { k: "bank_verified", Icon: Landmark },
    { k: "regulated_payments", Icon: ShieldQuestion },
    { k: "verified_transport", Icon: Truck },
    { k: "tracking", Icon: MapPin },
    { k: "single_contact", Icon: User },
  ];
  return (
    <div className="py-6 md:py-12 max-w-5xl" data-testid="secure-trade-page">
      <Link
        to="/home"
        className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-900 mb-6"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        {t("common.back")}
      </Link>

      <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400 font-semibold">
        {t("secure_trade.overline")}
      </p>
      <h1 className="mt-1 font-heading font-bold tracking-tighter text-3xl md:text-4xl lg:text-5xl text-slate-950">
        {t("secure_trade.title")}
      </h1>
      <p className="mt-3 text-sm text-slate-600 max-w-2xl leading-relaxed italic">
        {t("secure_trade.tagline")}
      </p>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="mt-10 rounded-2xl bg-[#0A0A0B] text-white p-6 md:p-10 relative overflow-hidden"
      >
        <div className="absolute -right-16 -bottom-16 w-64 h-64 rounded-full bg-blue-600/25 blur-3xl" />
        <div className="relative z-10 max-w-3xl">
          <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-blue-300 mb-3">
            <Handshake className="w-3.5 h-3.5" /> {t("secure_trade.positioning_overline")}
          </div>
          <h2 className="font-heading font-bold text-2xl md:text-3xl tracking-tight leading-tight">
            {t("secure_trade.positioning_title")}
          </h2>
          <p className="text-sm text-slate-300 mt-3 leading-relaxed">
            {t("secure_trade.positioning_desc")}
          </p>
        </div>
      </motion.div>

      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {pillars.map(({ k, Icon }, i) => (
          <motion.div
            key={k}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: i * 0.04 }}
            data-testid={`secure-trade-pillar-${k}`}
            className="rounded-2xl bg-white border border-slate-100 p-5 shadow-sm"
          >
            <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
              <Icon className="w-5 h-5" />
            </div>
            <h3 className="mt-3 font-heading font-semibold text-base tracking-tight">
              {t(`secure_trade.pillars.${k}.title`)}
            </h3>
            <p className="mt-1 text-sm text-slate-600 leading-relaxed">
              {t(`secure_trade.pillars.${k}.desc`)}
            </p>
          </motion.div>
        ))}
      </div>

      <div className="mt-8 rounded-xl bg-slate-50 border border-slate-200 p-4 text-xs text-slate-600 leading-relaxed">
        {t("secure_trade.disclaimer_note")}
      </div>
    </div>
  );
}
